import { memo, useMemo } from "react";

import { area, curveCatmullRom, line } from "d3-shape";
import { useShallow } from "zustand/react/shallow";

import { createXScale, createYScale } from "../../../helpers/d3.js";
import useStore, { useProjectedLocation } from "../../../store/store.js";

import style from "./PaceProfile.style.js";

const EMPTY_ARRAY = [];

const WIDTH = 300;
const HEIGHT = 70;
const VPAD = 8;
const MAX_NAME_LEN = 12;
const MIN_GAP_PCT = 15;

const truncate = (str) =>
  str && str.length > MAX_NAME_LEN ? str.slice(0, MAX_NAME_LEN) + "…" : str;

function getPaceKmh(section) {
  if (section.totalDistance <= 0) return null;
  if (section.maxCompletionTime != null && section.maxCompletionTime > 0) {
    return (section.totalDistance / section.maxCompletionTime) * 3.6;
  }
  if (section.estimatedDuration > 0) {
    return (section.totalDistance / section.estimatedDuration) * 3.6;
  }
  return null;
}

// Static chart — only recomputes when sections or distances change (not on GPS ticks)
function buildStaticChart(sections, cumulativeDistances) {
  if (!sections?.length || !cumulativeDistances?.length) return null;

  const hasMaxTime = sections.every((s) => s.maxCompletionTime != null);

  const bars = sections
    .map((section) => {
      const pace = getPaceKmh(section);
      if (pace == null) return null;
      const startDist = cumulativeDistances[section.startIndex] ?? 0;
      const endDist =
        cumulativeDistances[section.endIndex] ??
        startDist + section.totalDistance;
      return {
        startDist,
        endDist,
        midDist: (startDist + endDist) / 2,
        pace,
        // Average combined (terrain + fatigue + circadian + weather) factor for
        // this section, vs paceFactor (terrain only) — the gap between them is
        // the non-terrain slowdown that raw pace numbers otherwise absorb.
        effort: section.effortFactor ?? section.paceFactor ?? 1,
        startLocation: section.startLocation || "",
      };
    })
    .filter(Boolean);

  if (!bars.length) return null;

  const totalDistM = cumulativeDistances[cumulativeDistances.length - 1];
  const paces = bars.map((b) => b.pace);
  const minPace = Math.min(...paces);
  const maxPace = Math.max(...paces);
  const paceRange = maxPace - minPace || 1;

  const scaleX = createXScale(
    { min: 0, max: totalDistM },
    { min: 0, max: WIDTH },
  );
  const scaleY = createYScale(
    { min: minPace - paceRange * 0.15, max: maxPace + paceRange * 0.15 },
    { min: HEIGHT, max: 0 },
  );

  // One curve point per section at midpoint distance, anchored to route edges
  const curvePoints = [
    { x: scaleX(bars[0].startDist), y: scaleY(bars[0].pace) },
    ...bars.map((b) => ({ x: scaleX(b.midDist), y: scaleY(b.pace) })),
    {
      x: scaleX(bars[bars.length - 1].endDist),
      y: scaleY(bars[bars.length - 1].pace),
    },
  ];

  const linePath = line()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(curveCatmullRom.alpha(0.5))(curvePoints);

  const areaPath = area()
    .x((d) => d.x)
    .y1((d) => d.y)
    .y0(scaleY(minPace - paceRange * 0.15))
    .curve(curveCatmullRom.alpha(0.5))(curvePoints);

  // Effort trend — its own y-scale since values (~1.0-3.0x) share no unit with
  // km/h pace; only the x-axis (distance) is shared with the pace curve.
  const efforts = bars.map((b) => b.effort);
  const minEffort = Math.min(...efforts);
  const maxEffort = Math.max(...efforts);
  const effortRange = maxEffort - minEffort || 1;
  const scaleYEffort = createYScale(
    {
      min: minEffort - effortRange * 0.15,
      max: maxEffort + effortRange * 0.15,
    },
    { min: HEIGHT, max: 0 },
  );
  const effortCurvePoints = [
    { x: scaleX(bars[0].startDist), y: scaleYEffort(bars[0].effort) },
    ...bars.map((b) => ({ x: scaleX(b.midDist), y: scaleYEffort(b.effort) })),
    {
      x: scaleX(bars[bars.length - 1].endDist),
      y: scaleYEffort(bars[bars.length - 1].effort),
    },
  ];
  const effortLinePath = line()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(curveCatmullRom.alpha(0.5))(effortCurvePoints);

  // Tightest section = highest required pace (hardest cutoff)
  const tightestBar = bars.reduce(
    (max, b) => (b.pace > max.pace ? b : max),
    bars[0],
  );

  // Distance-weighted average required pace across all sections
  const totalDist = bars.reduce((sum, b) => sum + (b.endDist - b.startDist), 0);
  const avgPace =
    bars.reduce((sum, b) => sum + b.pace * (b.endDist - b.startDist), 0) /
    totalDist;

  // Distance-weighted average effort factor across all sections
  const avgEffort =
    bars.reduce((sum, b) => sum + b.effort * (b.endDist - b.startDist), 0) /
    totalDist;

  // Section boundary labels — deduplicate overlapping labels
  let lastLabelPct = -MIN_GAP_PCT;
  const sectionMarkersBase = bars
    .slice(1)
    .map((bar) => {
      const x = scaleX(bar.startDist);
      return { x, pct: (x / WIDTH) * 100, name: truncate(bar.startLocation) };
    })
    .filter((s) => {
      if (s.pct - lastLabelPct < MIN_GAP_PCT) return false;
      lastLabelPct = s.pct;
      return true;
    });

  return {
    bars,
    scaleX,
    curvePoints,
    linePath,
    areaPath,
    minPace,
    maxPace,
    totalDistKm: totalDistM / 1000,
    hasMaxTime,
    tightestBar,
    avgPace,
    sectionMarkersBase,
    effortLinePath,
    minEffort,
    maxEffort,
    avgEffort,
  };
}

// Dynamic overlay — recomputes on every GPS tick, but cheap
function buildOverlay(staticChart, projectedIndex, cumulativeDistances) {
  if (!staticChart) return null;
  const { bars, scaleX, curvePoints, sectionMarkersBase } = staticChart;

  let markerX = null;
  let markerY = null;
  let markerPct = null;
  let runnerDistKm = null;
  let currentBar = null;

  if (projectedIndex !== null && cumulativeDistances[projectedIndex] != null) {
    const distM = cumulativeDistances[projectedIndex];
    markerX = scaleX(distM);
    markerPct = (markerX / WIDTH) * 100;
    runnerDistKm = Math.round(distM / 1000);
    currentBar =
      bars.find((b) => distM >= b.startDist && distM < b.endDist) ??
      bars[bars.length - 1];

    // Interpolate Y on the curve between the two nearest midpoint samples
    const left = curvePoints.filter((p) => p.x <= markerX).at(-1);
    const right = curvePoints.find((p) => p.x >= markerX);
    if (left && right && right.x !== left.x) {
      const t = (markerX - left.x) / (right.x - left.x);
      markerY = left.y + t * (right.y - left.y);
    } else {
      markerY = (left ?? right)?.y ?? null;
    }
  }

  // Filter section labels too close to the runner marker
  const sectionMarkers = sectionMarkersBase.filter(
    (s) => markerPct === null || Math.abs(s.pct - markerPct) >= MIN_GAP_PCT,
  );

  return {
    markerX,
    markerY,
    markerPct,
    runnerDistKm,
    currentBar,
    sectionMarkers,
  };
}

const PaceProfile = memo(function PaceProfile({ className }) {
  const { sections, cumulativeDistances } = useStore(
    useShallow((state) => ({
      sections: state.sections,
      cumulativeDistances: state.gpx.cumulativeDistances || EMPTY_ARRAY,
    })),
  );
  const projectedLocation = useProjectedLocation();
  const projectedIndex = projectedLocation?.index ?? null;

  const staticChart = useMemo(
    () => buildStaticChart(sections, cumulativeDistances),
    [sections, cumulativeDistances],
  );

  const overlay = useMemo(
    () => buildOverlay(staticChart, projectedIndex, cumulativeDistances),
    [staticChart, projectedIndex, cumulativeDistances],
  );

  if (!staticChart || !overlay) return null;

  const {
    linePath,
    areaPath,
    minPace,
    maxPace,
    totalDistKm,
    hasMaxTime,
    tightestBar,
    avgPace,
    effortLinePath,
    maxEffort,
    avgEffort,
  } = staticChart;
  const {
    markerX,
    markerY,
    markerPct,
    runnerDistKm,
    sectionMarkers,
    currentBar,
  } = overlay;

  const displayPace = currentBar?.pace ?? avgPace;
  const displayLabel = currentBar ? "current" : "avg required";
  const displayEffort = currentBar?.effort ?? avgEffort;

  const ariaLabel = currentBar
    ? `Pace profile: required pace ranges from ${minPace.toFixed(1)} to ${maxPace.toFixed(1)} km/h. Currently in ${truncate(currentBar.startLocation) || "an unnamed section"} at ${currentBar.pace.toFixed(1)} km/h. Tightest section is ${truncate(tightestBar.startLocation) || "unnamed"} at ${tightestBar.pace.toFixed(1)} km/h.`
    : `Pace profile: required pace ranges from ${minPace.toFixed(1)} to ${maxPace.toFixed(1)} km/h. Tightest section is ${truncate(tightestBar.startLocation) || "unnamed"} at ${tightestBar.pace.toFixed(1)} km/h.`;

  return (
    <div className={className}>
      <div className="pp-header">
        <span className="pp-header-label">
          {hasMaxTime ? "Slowest Allowed Pace" : "Estimated Pace"}
        </span>
        <span className="pp-legend">
          <span className="pp-legend-item">
            <span className="pp-legend-swatch pp-legend-swatch--pace" /> pace
          </span>
          <span className="pp-legend-item">
            <span className="pp-legend-swatch pp-legend-swatch--effort" />{" "}
            effort
          </span>
        </span>
      </div>

      <div className="pp-chart">
        <svg
          role="img"
          aria-label={ariaLabel}
          viewBox={`0 -${VPAD} ${WIDTH} ${HEIGHT + VPAD * 2}`}
          preserveAspectRatio="none"
          width="100%"
          style={{ aspectRatio: `${WIDTH} / ${HEIGHT + VPAD * 2}` }}
        >
          {areaPath && <path className="pp-area" d={areaPath} stroke="none" />}
          {linePath && (
            <path
              className="pp-line"
              d={linePath}
              fill="none"
              strokeWidth="1.5"
            />
          )}
          {effortLinePath && (
            <path
              className="pp-effort-line"
              d={effortLinePath}
              fill="none"
              strokeWidth="1.25"
              strokeDasharray="4 2"
            />
          )}

          {/* Section boundary lines */}
          {sectionMarkers.map((s, i) => (
            <line
              key={i}
              className="pp-section-line"
              x1={s.x}
              y1={-VPAD}
              x2={s.x}
              y2={HEIGHT + VPAD}
              strokeWidth="1"
              strokeDasharray="2 3"
            />
          ))}

          {/* Runner position */}
          {markerX !== null && (
            <>
              <line
                className="pp-runner-line"
                x1={markerX}
                y1={-VPAD}
                x2={markerX}
                y2={HEIGHT + VPAD}
                strokeWidth="1.5"
                strokeDasharray="3 2"
              />
              {markerY !== null && (
                <circle
                  className="pp-runner-dot"
                  cx={markerX}
                  cy={markerY}
                  r={3}
                />
              )}
            </>
          )}
        </svg>

        {/* Axis labels — HTML overlay to avoid SVG text distortion */}
        <div className="pp-overlay">
          <span className="pp-label pp-label--tl">
            {maxPace.toFixed(1)} km/h
          </span>
          <span className="pp-label pp-label--tr pp-label--effort">
            {maxEffort.toFixed(1)}×
          </span>
          <span className="pp-label pp-label--bl">
            {minPace.toFixed(1)} km/h
          </span>
          <span className="pp-label pp-label--br">
            {totalDistKm.toFixed(0)} km
          </span>
        </div>
      </div>

      {/* Section names + runner distance below chart */}
      <div className="pp-bottom-labels">
        {sectionMarkers.map((s, i) => (
          <span
            key={i}
            className="pp-section-name"
            style={{ left: `${s.pct}%`, transform: "translateX(-50%)" }}
          >
            {s.name}
          </span>
        ))}
        {markerPct !== null && runnerDistKm != null && (
          <span
            className="pp-runner-value"
            style={{
              left: `clamp(12px, ${markerPct}%, calc(100% - 12px))`,
              transform: "translateX(-50%)",
            }}
          >
            {runnerDistKm} km
          </span>
        )}
      </div>

      <div className="pp-divider" />

      <div className="pp-stats">
        <div className="pp-stat">
          <span className="pp-stat-value pp-stat-value--tight">
            {tightestBar.pace.toFixed(1)}
            <span className="pp-stat-unit"> km/h</span>
          </span>
          <span className="pp-stat-label">tightest</span>
          <span className="pp-stat-name">
            {truncate(tightestBar.startLocation) || "—"}
          </span>
        </div>

        <div className="pp-stat-sep" />

        <div className="pp-stat">
          <span
            className={`pp-stat-value${currentBar ? " pp-stat-value--current" : ""}`}
          >
            {displayPace.toFixed(1)}
            <span className="pp-stat-unit"> km/h</span>
          </span>
          <span className="pp-stat-label">{displayLabel}</span>
          {currentBar && (
            <span className="pp-stat-name">
              {truncate(currentBar.startLocation) || "—"}
            </span>
          )}
        </div>

        <div className="pp-stat-sep" />

        <div className="pp-stat">
          <span className="pp-stat-value pp-stat-value--effort">
            {displayEffort.toFixed(1)}
            <span className="pp-stat-unit">×</span>
          </span>
          <span className="pp-stat-label">effort</span>
        </div>
      </div>
    </div>
  );
});

const StyledPaceProfile = style(PaceProfile);

export default StyledPaceProfile;
